import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { EuroIcon, RefreshCw, ExternalLink, AlertTriangle } from "lucide-react";

/**
 * Vista admin "Senza prezzo": elenca ogni formato che ha
 * `base_price_public = 0` E `price_override` nullo/0. Consente al cliente
 * di individuare a colpo d'occhio i vini che il sync Mexal (listino attuale)
 * non ha coperto o le schede manuali/file dove nessuno ha ancora inserito
 * un prezzo.
 */
export default function AdminMissingPrice() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/admin/wines/missing-price")
            .then(({ data }) => setData(data))
            .catch(() => toast.error("Errore caricamento elenco senza prezzo"))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    return (
        <div className="p-6 max-w-6xl mx-auto" data-testid="admin-missing-price-page">
            <div className="flex items-center justify-between mb-4 gap-3">
                <div>
                    <h1 className="text-2xl flex items-center gap-2">
                        <EuroIcon className="w-5 h-5" strokeWidth={1.5} />
                        Vini senza prezzo pubblico
                    </h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
                        Formati con <code>base_price_public = 0</code> e nessun{" "}
                        <code>price_override</code>. Sono i vini che il sync Mexal
                        (listino corrente) non ha coperto, o le schede manuali/file
                        dove nessuno ha ancora impostato un prezzo. Sistema il
                        prezzo direttamente in Mexal (listino 6 in produzione) —
                        il prossimo sync li aggiorna — oppure imposta un{" "}
                        <code>price_override</code> dalla scheda del vino.
                    </p>
                </div>
                <button
                    className="co-btn-ghost"
                    onClick={load}
                    disabled={loading}
                    data-testid="missing-price-refresh"
                >
                    <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
                    {loading ? "Ricarico…" : "Ricarica"}
                </button>
            </div>

            {!data ? (
                <div className="text-sm text-[hsl(var(--muted-foreground))]">Caricamento…</div>
            ) : (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                        <Stat label="Vini con almeno un formato a 0" value={data.wines} />
                        <Stat label="Formati totali a 0" value={data.formats} highlight />
                    </div>

                    {data.items.length === 0 ? (
                        <div className="p-6 bg-[#FDFBF7] border border-[hsl(var(--border))] rounded-sm text-center" data-testid="missing-price-empty">
                            <div className="text-lg mb-1">Nessun formato a 0 in questo momento.</div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))]">
                                Tutta la carta ha un prezzo pubblico effettivo.
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white border border-[hsl(var(--border))] rounded-sm overflow-x-auto">
                            <table className="w-full text-sm" data-testid="missing-price-table">
                                <thead className="bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] text-[11px] uppercase tracking-wider">
                                    <tr>
                                        <th className="text-left p-3">Produttore · Vino</th>
                                        <th className="text-left p-3">SKU</th>
                                        <th className="text-left p-3">Formato</th>
                                        <th className="text-right p-3">Stock</th>
                                        <th className="text-left p-3">Origine</th>
                                        <th className="text-left p-3">Status</th>
                                        <th className="text-right p-3">Azione</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[hsl(var(--border))]">
                                    {data.items.flatMap((it) =>
                                        it.formats_without_price.map((f, idx) => (
                                            <tr key={`${it.id}-${idx}`} data-testid={`missing-price-row-${it.id}-${idx}`}>
                                                <td className="p-3">
                                                    <div className="font-medium">{it.producer || "—"}</div>
                                                    <div className="text-[11px] text-[hsl(var(--muted-foreground))]">
                                                        {it.name}
                                                        {it.vintage && <span className="italic ml-1">{it.vintage}</span>}
                                                    </div>
                                                </td>
                                                <td className="p-3 font-mono text-xs">{f.mexal_sku || "—"}</td>
                                                <td className="p-3">{f.size} L</td>
                                                <td className="p-3 text-right">
                                                    {f.stock_quantity == null
                                                        ? <span className="text-[hsl(var(--muted-foreground))] italic">n/d</span>
                                                        : f.stock_quantity}
                                                </td>
                                                <td className="p-3 text-[11px] uppercase tracking-wider">
                                                    <SourceBadge source={it.source} />
                                                </td>
                                                <td className="p-3 text-[11px]">{it.status || "—"}</td>
                                                <td className="p-3 text-right">
                                                    <Link
                                                        to={`/admin/vini/${it.id}`}
                                                        className="inline-flex items-center gap-1 text-[hsl(var(--primary))] hover:underline"
                                                        data-testid={`missing-price-edit-${it.id}`}
                                                    >
                                                        Apri scheda <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {data.items.length > 0 && (
                        <div className="mt-4 flex items-start gap-2 text-xs text-[hsl(var(--muted-foreground))] p-3 border border-amber-200 bg-amber-50 rounded-sm" data-testid="missing-price-hint">
                            <AlertTriangle className="w-4 h-4 shrink-0 text-amber-700" strokeWidth={1.5} />
                            <div>
                                <b>Suggerimento.</b> Se il vino ha origine{" "}
                                <code>mexal</code>, il listino corrente in Mexal
                                non è compilato per quello SKU: completa il
                                listino sul gestionale e rilancia{" "}
                                <Link to="/admin/mexal" className="underline">
                                    Sync prezzi
                                </Link>. Se è <code>manual</code> o{" "}
                                <code>file</code>, apri la scheda e imposta un{" "}
                                <code>price_override</code>.
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function Stat({ label, value, highlight = false }) {
    return (
        <div className={`p-4 rounded-sm border ${highlight ? "border-[hsl(var(--primary))] bg-[#FDFBF7]" : "border-[hsl(var(--border))] bg-white"}`}>
            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-1">{label}</div>
            <div className={`text-2xl ${highlight ? "text-[hsl(var(--primary))]" : ""}`}>{value ?? 0}</div>
        </div>
    );
}

function SourceBadge({ source }) {
    const map = {
        mexal: "bg-blue-100 text-blue-800",
        manual: "bg-emerald-100 text-emerald-800",
        file: "bg-amber-100 text-amber-800",
    };
    const cls = map[source] || "bg-gray-100 text-gray-700";
    return (
        <span className={`inline-block px-1.5 py-0.5 rounded-sm ${cls}`}>
            {source || "?"}
        </span>
    );
}
